import { initSentry } from '@/lib/sentry';

// Initialize Sentry before rendering
initSentry();

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './index.css';
import { AuthProvider } from '@/contexts/AuthContext';
import { SidebarProvider } from '@/contexts/SidebarContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { RoleBasedRoute } from '@/components/RoleBasedRoute';
import { AppLayout } from '@/components/layout';
import { LoginPage } from '@/pages/LoginPage';
import { HomePage } from '@/pages/HomePage';
import { AdminUsersPage } from '@/pages/AdminUsersPage';

import { AdminHomepageContentPage } from '@/pages/AdminHomepageContentPage';
import { AdminTeamsPage } from '@/pages/AdminTeamsPage';
import { AdminTeamDetailsPage } from '@/pages/AdminTeamDetailsPage';
import { AdminJiraStatusMapPage } from '@/pages/AdminJiraStatusMapPage';
import { AdminAiCostPage } from '@/pages/AdminAiCostPage';
import { AdminLoginActivityPage } from '@/pages/AdminLoginActivityPage';
import { AdminScriptsPage } from '@/pages/AdminScriptsPage';
import { AdminLogsPage } from '@/pages/AdminLogsPage';
import { AdminSettingsPage } from '@/pages/AdminSettingsPage';
import { AdminPeoplePage } from '@/pages/AdminPeoplePage';
import { AdminTeamMappingPage } from '@/pages/AdminTeamMappingPage';
import { OrgHierarchyPage } from '@/pages/OrgHierarchyPage';
import { ScheduledJobsPage } from '@/pages/ScheduledJobsPage';
import { FabricTableBrowserPage } from '@/pages/FabricTableBrowserPage';
import { RepositoryConnectionsPage } from '@/pages/RepositoryConnectionsPage';
import { RepositoryDetailsPage } from '@/pages/RepositoryDetailsPage';
import { IncidentsPage } from '@/pages/IncidentsPage';
import { IssueCurrentPage } from '@/pages/IssueCurrentPage';
import { IssueInProgressDailyPage } from '@/pages/IssueInProgressDailyPage';
import { TblDeploymentsPage } from '@/pages/TblDeploymentsPage';
import { TblPullRequestsPage } from '@/pages/TblPullRequestsPage';

import { TblDevExQuarterlyNormalizedPage } from '@/pages/TblDevExQuarterlyNormalizedPage';
import { TblRawDevExQuarterlyPage } from '@/pages/TblRawDevExQuarterlyPage';
import { DevExTeamMappingPage } from '@/pages/DevExTeamMappingPage';
import { TblDimIssuePage } from '@/pages/TblDimIssuePage';
import { TblJiraIssuesPage } from '@/pages/TblJiraIssuesPage';
import { TblJiraStatusChangesPage } from '@/pages/TblJiraStatusChangesPage';
import { TblDimProductPage } from '@/pages/TblDimProductPage';
import { TblDimRepositoryPage } from '@/pages/TblDimRepositoryPage';
import { TblDimTeamPage } from '@/pages/TblDimTeamPage';
import { TblBridgeRepositoryProductPage } from '@/pages/TblBridgeRepositoryProductPage';
import { TblSonarqubeProjectsPage } from '@/pages/TblSonarqubeProjectsPage';
import { TblSonarqubeQualityLatestPage } from '@/pages/TblSonarqubeQualityLatestPage';
import { TblSonarqubeQualityHistoryPage } from '@/pages/TblSonarqubeQualityHistoryPage';
import { TblRawSyncStatusPage } from '@/pages/TblRawSyncStatusPage';
import { TblCuratedSyncStatusPage } from '@/pages/TblCuratedSyncStatusPage';
import { BimRelationshipsPage } from '@/pages/BimRelationshipsPage';
import { MetricViewPage } from '@/pages/MetricViewPage';
import { PeopleDirectoryPage } from '@/pages/PeopleDirectoryPage';
import { TeamVisualizationPage } from '@/pages/TeamVisualizationPage';
import { QualityControlPage } from '@/pages/QualityControlPage';
import { PipelineStatusPage } from '@/pages/PipelineStatusPage';
import { DebugSentryPage } from '@/pages/DebugSentryPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { ManagementViewPage } from '@/pages/ManagementViewPage';
import { MetricCataloguePage } from '@/pages/MetricCataloguePage';
import { AskPage } from '@/pages/AskPage';
import { MetricDefinitionsProvider } from '@/providers/MetricDefinitionsProvider';
import { AdminMetricDefinitionsPage } from '@/pages/AdminMetricDefinitionsPage';
import { AdminMetricGroupsPage } from '@/pages/AdminMetricGroupsPage';
import { validateNavigationConfig } from '@/lib/navigation';



import { navigationConfig } from '@/config/navigation';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { LegacyMetricsGuideRedirect } from '@/components/LegacyMetricsGuideRedirect';

// Validate navigation config at startup
// This will throw an error and prevent the app from loading if config is invalid
try {
  validateNavigationConfig(navigationConfig);
} catch(e) {
  console.error("Navigation config invalid:", e);
}

function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <SidebarProvider>
            <Routes>

            <Route path="/login" element={<LoginPage />} />
            <Route
              element={
                <ProtectedRoute>
                  <MetricDefinitionsProvider>
                    <AppLayout />
                  </MetricDefinitionsProvider>
                </ProtectedRoute>
              }
            >
              {/* Nested routes render inside AppLayout's <Outlet /> */}
              <Route path="/" element={<HomePage />} />
              <Route path="/ask" element={<AskPage />} />
              <Route path="/management-view" element={<ManagementViewPage />} />
              <Route path="/metric-catalogue" element={<MetricCataloguePage />} />
              <Route path="/metrics-guide" element={<LegacyMetricsGuideRedirect />} />
              <Route path="/metric-view" element={<MetricViewPage />} />
              {/* All authenticated users — guarded by the parent layout only,
                  same as /metric-view, /metric-catalogue, /ask. */}
              <Route path="/people" element={<PeopleDirectoryPage />} />
              {/* All authenticated users — bare route (a RoleBasedRoute with an
                  empty requiredRoles array would deny everyone). */}
              <Route path="/teams" element={<TeamVisualizationPage />} />
              {/* Users routes */}
              {/* Admin routes - protected by role */}
              <Route
                path="/admin/users"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin']}>
                    <AdminUsersPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/org-hierarchy"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <OrgHierarchyPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/homepage-content"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <AdminHomepageContentPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/teams"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <AdminTeamsPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/teams/:id"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <AdminTeamDetailsPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/map-jira-status"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <AdminJiraStatusMapPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/scheduled-jobs"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <ScheduledJobsPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/table-browser"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <FabricTableBrowserPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/repository-connections"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <RepositoryConnectionsPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/repositories/:id"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <RepositoryDetailsPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/tbl-incidents"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <IncidentsPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/tbl-issue-current"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <IssueCurrentPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/tbl-issue-in-progress-daily"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <IssueInProgressDailyPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/tbl-deployments"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <TblDeploymentsPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/tbl-pull-requests"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <TblPullRequestsPage />
                  </RoleBasedRoute>
                }
              />

              <Route
                path="/admin/tbl-devex-quarterly-raw"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <TblRawDevExQuarterlyPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/tbl-devex-quarterly-normalized"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <TblDevExQuarterlyNormalizedPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/devex-team-mapping"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <DevExTeamMappingPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/tbl-dim-issue"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <TblDimIssuePage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/tbl-jira-issues"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <TblJiraIssuesPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/tbl-jira-status-changes"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <TblJiraStatusChangesPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/tbl-dim-product"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <TblDimProductPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/tbl-dim-repository"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <TblDimRepositoryPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/tbl-dim-team"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <TblDimTeamPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/tbl-bridge-repository-product"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <TblBridgeRepositoryProductPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/tbl-dim-sonarqube-project"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <TblSonarqubeProjectsPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/tbl-fact-sonarqube-project-quality-latest"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <TblSonarqubeQualityLatestPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/tbl-fact-sonarqube-project-quality-history"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <TblSonarqubeQualityHistoryPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/tbl-sync-status-raw"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <TblRawSyncStatusPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/tbl-sync-status-curated"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <TblCuratedSyncStatusPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/bim-relationships"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <BimRelationshipsPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/quality-control"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <QualityControlPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/pipeline-status"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <PipelineStatusPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/metric-definitions"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <AdminMetricDefinitionsPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/metric-groups"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <AdminMetricGroupsPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/settings"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin']}>
                    <AdminSettingsPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/ai-cost"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin']}>
                    <AdminAiCostPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/login-activity"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin']}>
                    <AdminLoginActivityPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/scripts"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin']}>
                    <AdminScriptsPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/logs"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin']}>
                    <AdminLogsPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/people"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <AdminPeoplePage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/admin/team-mapping"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin', 'DataQualityAdmin']}>
                    <AdminTeamMappingPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/debug-sentry"
                element={
                  <RoleBasedRoute requiredRoles={['SiteAdmin']}>
                    <DebugSentryPage />
                  </RoleBasedRoute>
                }
              />
              {/* Catch-all 404 */}
              <Route path="*" element={<NotFoundPage />} />
            </Route>
            </Routes>
          </SidebarProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
    </ErrorBoundary>
  );
}


const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found. Ensure there is a <div id="root"> in index.html.');
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

